import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { Col, Row } from "antd/es/grid";
import Select from "antd/es/select";
import { defineMessages, useIntl } from "react-intl";

import FieldCategory from "@mapmycustomers/shared/enum/fieldModel/FieldCategory";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import {
  EntitiesSupportingBulkEdit,
  EntityTypesSupportingBulkEdit,
} from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { Alert, Button, SelectField } from "@mapmycustomers/ui";

import defaultFilterOption from "@app/component/input/utils/defaultFilterOption";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import useAnalytics from "@app/util/contexts/useAnalytics";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { isCustomField, isUniqueCustomField } from "@app/util/fieldModel/impl/assert";
import { getFieldCategoryDisplayName } from "@app/util/ui";

import { isUpdating } from "../../store";
import { updateEntities } from "../../store/actions";

import styles from "./BulkEditForm.module.scss";
import FieldEditor from "./component/FieldEditor";

const messages = defineMessages({
  address: {
    id: "view.selectBar.bulkEdit.panel.address",
    defaultMessage: "Address",
    description: "Bulk Edit on a selected entities bar - panel - Address label",
  },
  applyButton: {
    id: "view.selectBar.bulkEdit.panel.apply",
    defaultMessage: "Apply",
    description: "Bulk Edit on a selected entities bar - panel - Apply",
  },
  cancelButton: {
    id: "view.selectBar.bulkEdit.panel.cancel",
    defaultMessage: "Cancel",
    description: "Bulk Edit on a selected entities bar - panel - cancel",
  },
  containsRestrictedEditItems: {
    id: "view.selectBar.button.bulkEdit.panel.containsRestrictedEditItems",
    defaultMessage:
      "Please note, <b>one or more {entityType, select, trips {drive} other {record}} in this selection cannot be modified</b> as you don’t have permission to change them. ",
    description: "Info about restricted items for select field control",
  },
  selectField: {
    id: "view.selectBar.button.bulkEdit.panel.selectField",
    defaultMessage: "Select a field to update",
    description: "Bulk Edit button on a selected entities bar - panel - Select a field to update",
  },
  selectPlaceholder: {
    id: "view.selectBar.button.bulkEdit.panel.selectPlaceholder",
    defaultMessage: "Select",
    description: "Placeholder for select field control",
  },
});

type BulkEditProps = {
  containsRestrictedEditItems?: boolean;
  entities: Set<EntitiesSupportingBulkEdit>;
  entityType: EntityTypesSupportingBulkEdit;
  onHide: (updated: boolean) => void;
} & ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps;

const BulkEditForm: React.FC<BulkEditProps> = ({
  containsRestrictedEditItems,
  entities,
  entityType,
  onHide,
  updateEntities,
  updating,
}) => {
  const intl = useIntl();
  const analyticsIssuer = useAnalytics();
  useEffect(() => {
    analyticsIssuer.initiated(["Bulk Edit"]);
  }, [analyticsIssuer]);

  const [selectedFieldName, setSelectedFieldName] = useState<string | undefined>();
  const [value, setValue] = useState<unknown>();
  const [primaryEntityId, setPrimaryEntityId] = useState<number>();

  const fieldModel = getFieldModelByEntityType(entityType);

  const editableFields: Partial<Record<FieldCategory, IField[]>> = useMemo(
    () =>
      (Object.keys(fieldModel.categorizedFields) as FieldCategory[]).reduce(
        (result, category) => ({
          ...result,
          [category]: fieldModel.categorizedFields[category]!.filter(
            (field) =>
              field.isEditable &&
              (isCustomField(field) || field.hasFeature(FieldFeature.BULK_EDIT)) &&
              !isUniqueCustomField(field)
          ),
        }),
        {}
      ),
    [fieldModel]
  );

  const filteredCategories = useMemo(
    () =>
      editableFields
        ? (Object.keys(editableFields) as FieldCategory[]).filter(
            (category) => editableFields[category]?.length
          )
        : [],
    [editableFields]
  );

  const field = selectedFieldName ? fieldModel.getByName(selectedFieldName) : undefined;
  const isFieldSelected = !!field;

  const applyDisabled = useMemo(() => {
    if (updating) {
      return true;
    }

    // for frequency interval field we can apply none that is undefined
    if (field?.hasFeature(FieldFeature.FREQUENCY_INTERVAL)) {
      return false;
    }

    return value === undefined;
  }, [field, value, updating]);

  const handleSelectField = useCallback(
    (fieldName) => {
      setValue(undefined);
      setSelectedFieldName(fieldName);
    },
    [setValue]
  );

  const handleApply = useCallback(() => {
    if (!field) {
      return;
    }
    analyticsIssuer.completed(["Bulk Edit"]);

    const editableEntities = Array.from(entities).filter(
      (entity) => entity?.accessStatus?.update !== false
    );

    if (editableEntities.length) {
      updateEntities({
        entities: editableEntities,
        entityType,
        field,
        onSuccess: () => {
          onHide(true);
        },
        primaryEntityId,
        value,
      });
    }
  }, [
    analyticsIssuer,
    primaryEntityId,
    entities,
    entityType,
    field,
    onHide,
    updateEntities,
    value,
  ]);

  const handleCancel = useCallback(() => {
    analyticsIssuer.cancelled(["Bulk Edit"]);
    onHide(false);
  }, [onHide, analyticsIssuer]);

  return (
    <div className={styles.form}>
      {containsRestrictedEditItems && (
        <Alert
          message={intl.formatMessage(messages.containsRestrictedEditItems, {
            b: (text) => <b>{text}</b>,
            entityType,
          })}
          showIcon
          type="warning"
        />
      )}
      <SelectField<string>
        className={styles.dropdown}
        dropdownMatchSelectWidth={false}
        filterOption={defaultFilterOption}
        label={intl.formatMessage(messages.selectField)}
        onChange={handleSelectField}
        placeholder={intl.formatMessage(messages.selectPlaceholder)}
        showSearch
        value={selectedFieldName}
      >
        {filteredCategories.map((category) => (
          <Select.OptGroup key={category} label={getFieldCategoryDisplayName(intl, category)}>
            {editableFields[category]?.map((field) => (
              // We have inconsistent naming across for address fields for different entities.
              // Hence, here we do display hard-coded label.
              // Thus, we do support fields which are associated with ADDRESS category
              // (and in this context they also should have BULK_EDIT feature set).
              // There should be just one field which has BULK_EDIT feature set.
              // Other address-related fields will be populated based on user input.
              <Select.Option
                key={field.name}
                label={
                  field.hasFeature(FieldFeature.BULK_ADDRESS)
                    ? intl.formatMessage(messages.address)
                    : field.displayName
                }
                value={field.name}
              >
                {field.hasFeature(FieldFeature.BULK_ADDRESS)
                  ? intl.formatMessage(messages.address)
                  : field.displayName}
              </Select.Option>
            ))}
          </Select.OptGroup>
        ))}
      </SelectField>

      {isFieldSelected && (
        <FieldEditor
          entityType={entityType}
          field={field}
          onChange={setValue}
          onChangePrimaryEntityId={setPrimaryEntityId}
          value={value}
        />
      )}

      <Row gutter={layout.spacerS} justify="end">
        <Col>
          <Button onClick={handleCancel}>{intl.formatMessage(messages.cancelButton)}</Button>
        </Col>
        <Col>
          <Button disabled={applyDisabled} loading={updating} onClick={handleApply} type="primary">
            {intl.formatMessage(messages.applyButton)}
          </Button>
        </Col>
      </Row>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  updating: isUpdating(state),
});

const mapDispatchToProps = {
  updateEntities: updateEntities.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(BulkEditForm);
